import type { RecipientDetailRow } from "@/containers/whatsapp-broadcasts/context";
import type { BroadcastParameter } from "./utils";
import { MONTH_NAMES } from "@/utils/hijri-date-utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type ResolvePreviewVariablesArgs = {
  templateName?: string | null;
  parameters?: Record<string, BroadcastParameter> | null;
  firstRecipient?: (RecipientDetailRow & Record<string, unknown>) | null;
  firstCsvRow?: Record<string, string> | null;
  miqaatRecord?: Record<string, unknown> | null;
  eventRecord?: Record<string, unknown> | null;
};

export type ResolvePreviewVariablesResult = {
  variables: { value: string }[];
  isUsingActualData: boolean;
  rowSummaryLabel: string | null;
};

function formatCurrency(amount: unknown, fallback: string): string {
  if (amount !== undefined && amount !== null && !Number.isNaN(Number(amount))) {
    return `₹ ${Number(amount).toLocaleString("en-IN")}`;
  }
  return fallback;
}

function getRecipientName(
  recipient?: (RecipientDetailRow & Record<string, unknown>) | null,
  fallback = "Mufaddal Bhai"
): string {
  if (!recipient) return fallback;
  if (recipient.householdName && String(recipient.householdName).trim()) {
    return String(recipient.householdName).trim();
  }
  if (recipient.recipientName && String(recipient.recipientName).trim()) {
    return String(recipient.recipientName).trim();
  }
  if (recipient.Full_Name && recipient.Full_Name !== "-") return String(recipient.Full_Name);
  if (recipient.name && String(recipient.name).trim()) return String(recipient.name).trim();
  return fallback;
}

function getRecipientIts(
  recipient?: (RecipientDetailRow & Record<string, unknown>) | null,
  fallback = "60410992"
): string {
  if (!recipient) return fallback;
  if (recipient.householdItsNo && String(recipient.householdItsNo).trim()) {
    return String(recipient.householdItsNo).trim();
  }
  if (recipient.memberItsNo && String(recipient.memberItsNo).trim()) {
    return String(recipient.memberItsNo).trim();
  }
  if (recipient.itsNo && String(recipient.itsNo).trim()) return String(recipient.itsNo).trim();
  if (recipient.ITS_ID && recipient.ITS_ID !== "-") return String(recipient.ITS_ID);
  return fallback;
}

function getRecipientPhone(
  recipient?: (RecipientDetailRow & Record<string, unknown>) | null,
  fallback = "+91 98765 43210"
): string {
  if (!recipient) return fallback;
  if (recipient.phone && String(recipient.phone).trim()) return String(recipient.phone).trim();
  if (recipient.WhatsApp_No && String(recipient.WhatsApp_No).trim()) {
    return String(recipient.WhatsApp_No).trim();
  }
  if (recipient.Mobile && String(recipient.Mobile).trim()) return String(recipient.Mobile).trim();
  return fallback;
}

function formatMiqaatStartTimeLabel(hhmm: unknown, mealType: string): string {
  const raw =
    typeof hhmm === "string" && /^\d{2}:\d{2}$/.test(hhmm)
      ? hhmm
      : mealType === "LUNCH"
        ? "13:30"
        : "19:30";
  const d = dayjs(`2000-01-01T${raw}`, "YYYY-MM-DDTHH:mm", true);
  return d.isValid() ? d.format("h:mm A") : raw;
}

export function formatMiqaatSlotLabel(miqaat?: Record<string, unknown> | null): string {
  if (!miqaat) return "";
  // Prefer API-computed WhatsApp {{4}} when present (includes date + time).
  if (typeof miqaat.slotTypeDate === "string" && miqaat.slotTypeDate.trim()) {
    return miqaat.slotTypeDate.trim();
  }
  const timeLabel = formatMiqaatStartTimeLabel(miqaat.startTime, String(miqaat.mealType ?? ""));
  const dateMode = String(miqaat.dateMode || "");

  if (dateMode === "PERMANENT") {
    const m = typeof miqaat.hijriMonth === "number" ? miqaat.hijriMonth : 0;
    const d = typeof miqaat.hijriDay === "number" ? miqaat.hijriDay : 0;
    const monthLabel = m >= 0 && m < 12 ? (MONTH_NAMES.short.en[m] ?? `Month ${m + 1}`) : "—";
    return `Hijri ${d} ${monthLabel} at ${timeLabel}`;
  }

  if (miqaat.englishDate) {
    try {
      const d = dayjs(String(miqaat.englishDate));
      if (d.isValid()) {
        return `${d.format("DD MMM YYYY")} at ${timeLabel}`;
      }
    } catch {
      // ignore
    }
  }

  return `(Date TBD) at ${timeLabel}`;
}

export function resolvePreviewVariables({
  templateName,
  parameters,
  firstRecipient,
  firstCsvRow,
  miqaatRecord,
  eventRecord,
}: ResolvePreviewVariablesArgs): ResolvePreviewVariablesResult {
  const normTemplate = (templateName || "").toLowerCase();

  // 1. Explicit parameter mappings (Custom Broadcasts)
  if (parameters && typeof parameters === "object" && Object.keys(parameters).length > 0) {
    let usedActualData = false;

    const variables = Object.keys(parameters)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .map((key) => {
        const param = parameters[key];
        if (!param || typeof param !== "object") {
          return { value: String(param ?? "") };
        }

        if (param.type === "text") {
          const textVal = String(param.value ?? "");
          return { value: textVal || `{{${key}}}` };
        }

        if (param.type === "column") {
          const col = String(param.column || "").trim();

          // Check CSV source first if specified or available
          if (param.columnSource === "csv" || (firstCsvRow && col in firstCsvRow)) {
            if (
              firstCsvRow &&
              col in firstCsvRow &&
              firstCsvRow[col] !== undefined &&
              firstCsvRow[col] !== null &&
              String(firstCsvRow[col]).trim().length > 0
            ) {
              usedActualData = true;
              return { value: String(firstCsvRow[col]).trim() };
            }
            return { value: `[CSV: ${col || "Column"}]` };
          }

          // ITS / database directory column source
          if (firstRecipient) {
            let matchedVal: unknown;

            if (col === "Full_Name" || col === "name" || col === "recipientName") {
              matchedVal = getRecipientName(firstRecipient, "");
            } else if (
              col === "ITS_ID" ||
              col === "itsNo" ||
              col === "memberItsNo" ||
              col === "householdItsNo"
            ) {
              matchedVal = getRecipientIts(firstRecipient, "");
            } else if (col === "Mobile" || col === "WhatsApp_No" || col === "phone") {
              matchedVal = getRecipientPhone(firstRecipient, "");
            } else if (col in firstRecipient && firstRecipient[col] !== "-") {
              matchedVal = firstRecipient[col];
            } else if (firstCsvRow && col in firstCsvRow) {
              matchedVal = firstCsvRow[col];
            }

            if (
              matchedVal !== undefined &&
              matchedVal !== null &&
              String(matchedVal).trim().length > 0 &&
              matchedVal !== "-"
            ) {
              usedActualData = true;
              return { value: String(matchedVal).trim() };
            }
          }

          return { value: `[${col || "Column"}]` };
        }

        return { value: String(param.value ?? param.column ?? `{{${key}}}`) };
      })
      .filter((v) => v.value !== "");

    let rowSummaryLabel: string | null = null;
    if (usedActualData) {
      if (firstRecipient) {
        const name = getRecipientName(firstRecipient, "");
        const its = getRecipientIts(firstRecipient, "");
        if (name) {
          rowSummaryLabel = `Row 1: ${name}${its ? ` (${its})` : ""}`;
        } else {
          rowSummaryLabel = "Row 1 data";
        }
      } else if (firstCsvRow) {
        rowSummaryLabel = "Row 1 of CSV";
      } else {
        rowSummaryLabel = "Row 1 data";
      }
    }

    return {
      variables,
      isUsingActualData: usedActualData,
      rowSummaryLabel,
    };
  }

  // 2. Automated Meta Templates
  if (normTemplate) {
    const hasRecipient = Boolean(firstRecipient);
    const actualName = getRecipientName(firstRecipient, "");
    const actualIts = getRecipientIts(firstRecipient, "");
    const isActual = Boolean(
      hasRecipient &&
      (actualName ||
        actualIts ||
        firstRecipient?.takhmeenAmount != null ||
        firstRecipient?.pendingBalance != null)
    );

    const defaultDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    let rowSummaryLabel: string | null = null;
    if (isActual && firstRecipient) {
      const name = getRecipientName(firstRecipient, "");
      const its = getRecipientIts(firstRecipient, "");
      rowSummaryLabel = `Row 1: ${name || "Recipient"}${its ? ` (${its})` : ""}`;
    }

    if (
      normTemplate.includes("fmb_outstanding") ||
      normTemplate.includes("fmb_ledger_outstanding")
    ) {
      const its = getRecipientIts(firstRecipient, "60410992");
      const name = getRecipientName(firstRecipient, "Mufaddal Bhai");
      const due = formatCurrency(firstRecipient?.takhmeenAmount, "₹ 25,000");
      const paid = formatCurrency(firstRecipient?.paidBalance, "₹ 15,000");
      const pending = formatCurrency(firstRecipient?.pendingBalance, "₹ 10,000");

      return {
        variables: [
          { value: its },
          { value: name },
          { value: due },
          { value: paid },
          { value: pending },
        ],
        isUsingActualData: isActual,
        rowSummaryLabel,
      };
    }

    if (normTemplate.includes("sabil_establishment")) {
      const name = getRecipientName(firstRecipient, "Burhani Traders");
      const its = getRecipientIts(firstRecipient, "60410992");
      const asOf = String(firstRecipient?.asOfDate || defaultDate);
      const due = formatCurrency(firstRecipient?.takhmeenAmount, "₹ 25,000");
      const paid = formatCurrency(firstRecipient?.paidBalance, "₹ 15,000");
      const pending = formatCurrency(firstRecipient?.pendingBalance, "₹ 10,000");

      return {
        variables: [
          { value: name },
          { value: its },
          { value: asOf },
          { value: due },
          { value: paid },
          { value: pending },
        ],
        isUsingActualData: isActual,
        rowSummaryLabel,
      };
    }

    if (normTemplate.includes("sabil_chula") || normTemplate.includes("sabil_outstanding")) {
      const name = getRecipientName(firstRecipient, "Mufaddal Bhai");
      const its = getRecipientIts(firstRecipient, "60410992");
      const asOf = String(firstRecipient?.asOfDate || defaultDate);
      const monthly = formatCurrency(firstRecipient?.monthlyTakhmeenAmount, "₹ 1,500");
      const due = formatCurrency(firstRecipient?.takhmeenAmount, "₹ 18,000");
      const paid = formatCurrency(firstRecipient?.paidBalance, "₹ 10,000");
      const pending = formatCurrency(firstRecipient?.pendingBalance, "₹ 8,000");

      return {
        variables: [
          { value: name },
          { value: its },
          { value: asOf },
          { value: monthly },
          { value: due },
          { value: paid },
          { value: pending },
        ],
        isUsingActualData: isActual,
        rowSummaryLabel,
      };
    }

    if (normTemplate.includes("mutawatteen") || normTemplate.includes("muttavatteen")) {
      const name = getRecipientName(firstRecipient, "Mufaddal Bhai");
      const its = getRecipientIts(firstRecipient, "60410992");
      const asOf = String(firstRecipient?.asOfDate || defaultDate);
      const due = formatCurrency(firstRecipient?.takhmeenAmount, "₹ 12,000");
      const paid = formatCurrency(firstRecipient?.paidBalance, "₹ 8,000");
      const pending = formatCurrency(firstRecipient?.pendingBalance, "₹ 4,000");

      return {
        variables: [
          { value: name },
          { value: its },
          { value: asOf },
          { value: due },
          { value: paid },
          { value: pending },
        ],
        isUsingActualData: isActual,
        rowSummaryLabel,
      };
    }

    if (normTemplate.includes("rsvp")) {
      const name = getRecipientName(firstRecipient, "Mufaddal Bhai");
      let eventOrMiqaat = "Urs Mubarak - Syedna Taher Saifuddin RA";
      let venue = "Burhani Hall";
      let slotOrDate = "Hijri 19 Rajab at 1:30 PM";
      let hasActualEventData = false;

      if (miqaatRecord) {
        if (miqaatRecord.name && String(miqaatRecord.name).trim()) {
          eventOrMiqaat = String(miqaatRecord.name).trim();
          hasActualEventData = true;
        }
        if (miqaatRecord.venue && String(miqaatRecord.venue).trim()) {
          venue = String(miqaatRecord.venue).trim();
          hasActualEventData = true;
        }
        const formattedSlot = formatMiqaatSlotLabel(miqaatRecord);
        if (formattedSlot) {
          slotOrDate = formattedSlot;
          hasActualEventData = true;
        }
      } else if (eventRecord) {
        if (eventRecord.name && String(eventRecord.name).trim()) {
          eventOrMiqaat = String(eventRecord.name).trim();
          hasActualEventData = true;
        }
        if (eventRecord.venue && String(eventRecord.venue).trim()) {
          venue = String(eventRecord.venue).trim();
          hasActualEventData = true;
        } else if (eventRecord.markaz && String(eventRecord.markaz).trim()) {
          venue = String(eventRecord.markaz).trim();
          hasActualEventData = true;
        }
        if (eventRecord.eventDates && String(eventRecord.eventDates).trim()) {
          slotOrDate = String(eventRecord.eventDates).trim();
          hasActualEventData = true;
        } else if (eventRecord.date && String(eventRecord.date).trim()) {
          slotOrDate = String(eventRecord.date).trim();
          hasActualEventData = true;
        }
      } else if (firstRecipient?.eventName || firstRecipient?.miqaatName) {
        eventOrMiqaat = String(firstRecipient.eventName || firstRecipient.miqaatName);
        hasActualEventData = true;
        if (firstRecipient.markaz) {
          venue = String(firstRecipient.markaz);
        }
      }

      let label: string | null = null;
      if (isActual && rowSummaryLabel) {
        label = rowSummaryLabel;
      } else if (hasActualEventData) {
        label = miqaatRecord ? `Miqaat: ${eventOrMiqaat}` : `Event: ${eventOrMiqaat}`;
      }

      return {
        variables: [
          { value: name },
          { value: eventOrMiqaat },
          { value: venue },
          { value: slotOrDate },
        ],
        isUsingActualData: isActual || hasActualEventData,
        rowSummaryLabel: label,
      };
    }
  }

  // 3. Fallback: If template is custom but parameters haven't been mapped yet,
  // preview first recipient or CSV row if available.
  if (firstRecipient) {
    const name = getRecipientName(firstRecipient, "");
    const its = getRecipientIts(firstRecipient, "");
    const phone = getRecipientPhone(firstRecipient, "");
    if (name || its) {
      return {
        variables: [{ value: name || "Recipient" }, { value: its || "ITS" }, { value: phone }],
        isUsingActualData: true,
        rowSummaryLabel: `Row 1: ${name || "Recipient"}${its ? ` (${its})` : ""}`,
      };
    }
  }

  if (firstCsvRow) {
    const vals = Object.values(firstCsvRow).filter((v) => String(v).trim().length > 0);
    if (vals.length > 0) {
      return {
        variables: vals.map((v) => ({ value: String(v) })),
        isUsingActualData: true,
        rowSummaryLabel: "Row 1 of CSV",
      };
    }
  }

  return {
    variables: [],
    isUsingActualData: false,
    rowSummaryLabel: null,
  };
}
